import { useCallback, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { useSimulationStore } from '@/state/simulationStore';
import { DEMO_NOW } from '@/domain/demoClock';
import { advanceSimClock } from '@/domain/simClockFlow';

// Guard rails around the seeded data window. The forward fill covers DEMO_NOW
// (2026-05-04) through 2026-06-07 (~+34 days). Q7 (DECIDED 25 Sep, D28): the
// facilitator may step on to +120 days — long top-up plans, 12-month deposits
// and the Sweeper's 48h escalations need it — and past the seeded data the
// header shows a WARNING chip, never a block. +120 is the hard stop so the clock
// never runs unbounded. A few days before DEMO_NOW keeps a sane backward bound.
const SEED_FORWARD_MAX = 120; // whole days past DEMO_NOW
const SEED_BACK_MIN = -3;
const SEED_LAST_OFFSET = 34; // last day the forward fill authored sittings for

const MEL_YMD = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Australia/Melbourne',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const melYmd = (iso: string): string => MEL_YMD.format(new Date(iso));

const MEL_LABEL = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const clockLabel = (iso: string): string => MEL_LABEL.format(new Date(iso));

const DAY_MS = 24 * 60 * 60 * 1000;
/** Whole-day offset (Melbourne calendar days) of `iso` from DEMO_NOW. */
function offsetFromDemoNow(iso: string): number {
  const a = Date.parse(`${melYmd(iso)}T00:00:00Z`);
  const b = Date.parse(`${melYmd(DEMO_NOW)}T00:00:00Z`);
  return Math.round((a - b) / DAY_MS);
}
const clampOffset = (o: number): number =>
  Math.min(SEED_FORWARD_MAX, Math.max(SEED_BACK_MIN, o));

const isTypingTarget = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
};

/**
 * Facilitator-only day stepper over the sim clock: prev / next / jump-to-date /
 * reset all move `clockIso` by whole days (offset preserving), never firing a
 * scripted beat. One side effect: a FORWARD move goes through the ONE
 * day-advanced hook (simClockFlow.advanceSimClock, tv-2na.10.13): link expiry,
 * the top-up schedule and the T-4d waiver dispatch run on the new day
 * (server-side in the served build). Backward moves and reset never run it.
 */
export default function SimClockStepper() {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const stepDay = useSimulationStore((s) => s.stepDay);
  const resetClock = useSimulationStore((s) => s.resetClock);

  const offsetDays = offsetFromDemoNow(clockIso);
  const atForwardEdge = offsetDays >= SEED_FORWARD_MAX;
  const atBackEdge = offsetDays <= SEED_BACK_MIN;
  const beyondSeed = offsetDays > SEED_LAST_OFFSET;

  // Step by `delta` whole days, clamped to the seeded window so stepping can
  // never run unbounded.
  const stepBy = useCallback(
    (delta: number) => {
      const current = offsetFromDemoNow(useSimulationStore.getState().clockIso);
      const actual = clampOffset(current + delta) - current;
      // Forward: the ONE day-advanced hook (moves the clock, runs the bundle).
      if (actual > 0) void advanceSimClock({ days: actual });
      else if (actual < 0) stepDay(actual);
    },
    [stepDay],
  );

  const jumpToDate = useCallback(
    (ymd: string) => {
      if (!ymd) return;
      const targetOffset = Math.round(
        (Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${melYmd(DEMO_NOW)}T00:00:00Z`)) / DAY_MS,
      );
      const current = offsetFromDemoNow(useSimulationStore.getState().clockIso);
      const actual = clampOffset(targetOffset) - current;
      if (actual > 0) void advanceSimClock({ days: actual });
      else if (actual < 0) stepDay(actual);
    },
    [stepDay],
  );

  // Keyboard shortcuts: [ prev day, ] next day, T reset to today. Ignored while
  // typing in a field. Cleaned up on unmount.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === '[') {
        e.preventDefault();
        stepBy(-1);
      } else if (e.key === ']') {
        e.preventDefault();
        stepBy(1);
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        resetClock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepBy, resetClock]);

  const deltaLabel =
    offsetDays > 0 ? `SIM +${offsetDays}d` : `SIM −${Math.abs(offsetDays)}d`;

  return (
    <div
      className="flex items-center gap-2"
      data-testid="sim-stepper"
      title="Simulated studio day (facilitator control)"
    >
      <div className="flex items-center gap-1 px-1.5 py-1 rounded-full border border-gold-dim/30">
        <button
          type="button"
          onClick={() => stepBy(-1)}
          disabled={atBackEdge}
          className="btn-quiet px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="sim-step-prev"
          aria-label="Previous day"
          title="Previous day  [ [ ]"
        >
          &#9664;
        </button>

        <span
          className="font-mono text-sm text-gold-bright tabular-nums px-1 whitespace-nowrap"
          data-testid="sim-clock-label"
        >
          {clockLabel(clockIso)}
        </span>

        <button
          type="button"
          onClick={() => stepBy(1)}
          disabled={atForwardEdge}
          className="btn-quiet px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="sim-step-next"
          aria-label="Next day"
          title="Next day  [ ] ]"
        >
          &#9654;
        </button>
      </div>

      <input
        type="date"
        value={melYmd(clockIso)}
        onChange={(e) => jumpToDate(e.target.value)}
        className="bg-ink-dark border border-gold-dim/30 rounded-md text-cream-soft text-xs px-2 py-1 font-mono"
        data-testid="sim-jump-date"
        aria-label="Jump to date"
        title="Jump to a specific day"
      />

      {offsetDays !== 0 && (
        <button
          type="button"
          onClick={resetClock}
          className="pill-gold-solid !py-1 animate-gold-flash"
          data-testid="sim-delta"
          title="Time-travelled from today — click to reset"
        >
          {deltaLabel}
        </button>
      )}

      <button
        type="button"
        onClick={resetClock}
        className="btn-quiet px-2 py-1"
        data-testid="sim-reset"
        aria-label="Reset to today"
        title="Reset to today  [ T ]"
      >
        <RotateCcw size={14} />
      </button>

      {beyondSeed && (
        <span
          className="py-0.5 text-[10px] uppercase tracking-wider text-orange border border-orange/50 rounded-full px-2"
          data-testid="sim-seed-edge"
          role="status"
          title="The demo data is seeded up to 7 Jun. Past that, calendars start empty, but every flow still works."
        >
          past seeded data
        </span>
      )}
    </div>
  );
}
